/**
 * Customer-Side Supabase Operations
 * Production-ready setup for customer app
 * Matches caterer-side schema and functionality
 */

import { createClient } from '@supabase/supabase-js';
import { geocodePlace } from './geocoding';

export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const IS_PROD = import.meta.env.PROD;
const APP_URL = import.meta.env.VITE_APP_URL?.replace(/\/$/, '');
const FALLBACK_PRODUCTION_APP_URL = 'https://mrscater.com';

const resolveAuthRedirectBase = () => {
  if (APP_URL) return APP_URL;
  if (typeof window === 'undefined') return '';

  const { origin, hostname } = window.location;
  // Avoid shipping localhost reset links when production env vars are missing.
  if (IS_PROD && /^(localhost|127\.0\.0\.1)$/i.test(hostname)) return FALLBACK_PRODUCTION_APP_URL;

  return origin;
};

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  const message = 'Missing Supabase environment variables: VITE_SUPABASE_URL and/or VITE_SUPABASE_ANON_KEY.';
  if (IS_PROD) {
    // In production we fail fast so we don't talk to a placeholder backend.
    throw new Error(message);
  } else {
    // In development we log loudly but allow the app to start.
    // eslint-disable-next-line no-console
    console.error(message, {
      VITE_SUPABASE_URL: SUPABASE_URL || 'MISSING',
      VITE_SUPABASE_ANON_KEY: SUPABASE_ANON_KEY ? 'Present' : 'MISSING',
    });
  }
}

export const supabase = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
      storageKey: 'mrs-cater-customer-auth',
      flowType: 'pkce'
    },
    global: {
      headers: {
        'X-Client-Info': 'mrs-cater-customer@1.0.0'
      }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

/**
 * Normalise any stored storage path so that we can safely call
 * supabase.storage.from(bucket).getPublicUrl(normalizedPath).
 *
 * Handles all of these shapes:
 * - "caterers/<id>/logo-123.jpg"
 * - "/caterers/<id>/logo-123.jpg"
 * - "profile-images/caterers/<id>/logo-123.jpg"
 * - "/profile-images/caterers/<id>/logo-123.jpg"
 *
 * We always strip any leading slash and known bucket prefixes so that the
 * path we pass to Storage is relative to the bucket, regardless of how
 * it was stored in the DB.
 */
const normalizeStoragePath = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return null;
  let trimmed = rawPath.trim();
  if (!trimmed) return null;
  // Already a full URL or data URI – do not touch.
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  // Remove leading slash(es)
  while (trimmed.startsWith('/')) trimmed = trimmed.slice(1);
  const bucketPrefixes = ['profile-images/', 'portfolio-images/', 'menu-images/'];
  for (const prefix of bucketPrefixes) {
    if (trimmed.toLowerCase().startsWith(prefix)) {
      trimmed = trimmed.slice(prefix.length);
      break;
    }
  }
  return trimmed;
};

/**
 * Resolve caterer logo/storage path to public Supabase storage URL.
 *
 * Supports both legacy and new formats:
 * - plain relative paths: "caterers/<id>/logo-123.jpg"
 * - paths that already include bucket: "profile-images/caterers/<id>/logo-123.jpg"
 * - full external URLs: "https://…", "data:…"
 */
export const resolveCatererImageUrl = (path) => {
  if (!path) return null;
  const normalized = normalizeStoragePath(path);
  if (!normalized) return null;
  // If normalizeStoragePath returned a full URL, just use it directly.
  if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('data:')) {
    return normalized;
  }
  const { data } = supabase.storage.from('profile-images').getPublicUrl(normalized);
  return data?.publicUrl || null;
};

/**
 * ============================================================================
 * AUTHENTICATION OPERATIONS
 * ============================================================================
 */
export const customerAuthOperations = {
  /**
   * Sign up a new customer
   */
  signUp: async (email, password, fullName) => {
    // Create auth user. Store full name in user metadata.
    const authResult = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName }
      }
    });

    // For email confirmation flows, there may be no session yet.
    // Avoid inserting into protected tables here; we'll create the profile on first login.
    if (authResult.error) return authResult;
    return { data: { user: authResult.data.user }, error: null };
  },

  /**
   * Sign in existing customer
   */
  signIn: async (email, password) => {
    return supabase.auth.signInWithPassword({ email, password });
  },

  /**
   * Sign out customer
   */
  signOut: () => supabase.auth.signOut(),
  signOutLocal: () => supabase.auth.signOut({ scope: 'local' }),

  /**
   * Get current authenticated user
   */
  getCurrentUser: async () => {
    console.log('[Auth] Calling supabase.auth.getUser()...');
    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth timeout after 2 seconds')), 2000)
      );
      
      // Race between getUser and timeout
      const result = await Promise.race([
        supabase.auth.getUser(),
        timeoutPromise
      ]);
      
      console.log('[Auth] getUser result:', { 
        hasUser: !!result.data?.user, 
        hasError: !!result.error,
        errorMessage: result.error?.message 
      });
      return result;
    } catch (err) {
      console.error('[Auth] getUser exception:', err.message || err);
      // Return empty result so app can continue
      return { data: { user: null }, error: null };
    }
  },

  /**
   * Listen to auth state changes
   */
  onAuthStateChange: (callback) => supabase.auth.onAuthStateChange(callback),

  /**
   * Send password reset email
   */
  resetPassword: async (email) => {
    const redirectBase = resolveAuthRedirectBase();
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${redirectBase}/reset-password`
    });
  },

  /**
   * Update password
   */
  updatePassword: async (newPassword) => {
    return supabase.auth.updateUser({ password: newPassword });
  },
  /**
   * Verify email using OTP code from Supabase
   */
  verifyEmailOtp: async (email, token) => {
    return supabase.auth.verifyOtp({ type: 'email', email, token });
  },

  /**
   * Resend signup verification email
   */
  resendVerification: async (email) => {
    // For Supabase JS v2
    return supabase.auth.resend({ type: 'signup', email });
  }
};

/**
 * ============================================================================
 * REFERRAL OPERATIONS
 * ============================================================================
 */
const generateReferralCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
  return code;
};

export const customerReferralOperations = {
  getOrCreateCode: async (userId, role = 'customer') => {
    const { data: existing } = await supabase
      .from('referral_codes')
      .select('id, code')
      .eq('user_id', userId)
      .eq('role', role)
      .maybeSingle();
    if (existing) return { data: existing, error: null };
    const code = generateReferralCode();
    const { data: inserted, error } = await supabase
      .from('referral_codes')
      .insert({ user_id: userId, code, role })
      .select('id, code')
      .single();
    return { data: inserted || { code }, error };
  },
  getByCode: async (code) => {
    const { data, error } = await supabase
      .from('referral_codes')
      .select('id, user_id, role')
      .eq('code', String(code).toUpperCase().trim())
      .maybeSingle();
    return { data, error };
  },
  createRedemption: async (referralCodeId, referredUserId, rewardAmountCents = 5000) => {
    return supabase
      .from('referral_redemptions')
      .insert({ referral_code_id: referralCodeId, referred_user_id: referredUserId, reward_amount_cents: rewardAmountCents })
      .select('id')
      .single();
  },
  getMyStats: async (userId) => {
    const { data: codes } = await supabase.from('referral_codes').select('id, code, role').eq('user_id', userId);
    const codeIds = (codes || []).map((c) => c.id);
    if (codeIds.length === 0) return { data: { codes: [], redemptions: [], totalReferrals: 0 }, error: null };
    const { data: redemptions } = await supabase
      .from('referral_redemptions')
      .select('id, referred_user_id, reward_amount_cents, applied_at, created_at')
      .in('referral_code_id', codeIds)
      .order('created_at', { ascending: false });
    return {
      data: { codes: codes || [], redemptions: redemptions || [], totalReferrals: (redemptions || []).length },
      error: null
    };
  }
};

/**
 * ============================================================================
 * CUSTOMER PROFILE OPERATIONS
 * ============================================================================
 */
export const customerProfileOperations = {
  /**
   * Get customer profile
   */
  getProfile: async (userId) => {
    // Use maybeSingle to avoid 406 when no row exists yet
    const { data, error } = await supabase
      .from('customer_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    return { data, error };
  },

  /**
   * Create customer profile (usually done during signup)
   */
  createProfile: async (userId, profileData) => {
    return supabase
      .from('customer_profiles')
      .insert({
        user_id: userId,
        ...profileData,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Update customer profile
   */
  updateProfile: async (userId, profileData) => {
    return supabase
      .from('customer_profiles')
      .update({
        ...profileData,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);
  },

  /**
   * Get customer dashboard statistics
   */
  getDashboardStats: async (userId) => {
    try {
      const result = await supabase
        .from('customer_dashboard_stats')
        .select('*')
        .eq('customer_id', userId)
        .single();
      
      // Return empty stats if view doesn't exist
      if (result.error && (result.error.code === '42P01' || result.error.message?.includes('does not exist'))) {
        return {
          data: {
            total_events: 0,
            upcoming_events: 0,
            total_spent: 0,
            favorite_caterers: 0,
            events_trend: 0,
            spending_trend: 0,
            average_rating_given: 0
          },
          error: null
        };
      }
      
      return result;
    } catch (err) {
      console.error('[getDashboardStats] Error:', err);
      return {
        data: {
          total_events: 0,
          upcoming_events: 0,
          total_spent: 0,
          favorite_caterers: 0,
          events_trend: 0,
          spending_trend: 0,
          average_rating_given: 0
        },
        error: err
      };
    }
  },

  /**
   * Update notification preferences
   */
  updateNotificationPreferences: async (userId, preferences) => {
    return supabase
      .from('customer_profiles')
      .update({
        notifications_enabled: preferences.notifications_enabled,
        email_notifications: preferences.email_notifications,
        newsletter_subscribed: preferences.newsletter_subscribed,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);
  }
};

/**
 * ============================================================================
 * CUSTOMER ADDRESS OPERATIONS
 * ============================================================================
 */
export const customerAddressOperations = {
  /**
   * Get all addresses for customer
   */
  getAddresses: async (customerId) => {
    return supabase
      .from('customer_addresses')
      .select('*')
      .eq('customer_id', customerId)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: false });
  },

  /**
   * Get single address
   */
  getAddress: async (addressId, customerId) => {
    return supabase
      .from('customer_addresses')
      .select('*')
      .eq('id', addressId)
      .eq('customer_id', customerId)
      .single();
  },

  /**
   * Create new address
   */
  createAddress: async (customerId, addressData) => {
    return supabase
      .from('customer_addresses')
      .insert({
        customer_id: customerId,
        ...addressData,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Update address
   */
  updateAddress: async (addressId, customerId, addressData) => {
    return supabase
      .from('customer_addresses')
      .update({
        ...addressData,
        updated_at: new Date().toISOString()
      })
      .eq('id', addressId)
      .eq('customer_id', customerId);
  },

  /**
   * Delete address
   */
  deleteAddress: async (addressId, customerId) => {
    return supabase
      .from('customer_addresses')
      .delete()
      .eq('id', addressId)
      .eq('customer_id', customerId);
  },

  /**
   * Set as default address
   */
  setDefaultAddress: async (addressId, customerId) => {
    // Prefer atomic server-side function when available
    const { data, error } = await supabase.rpc('set_default_customer_address', {
      p_address_id: addressId,
      p_customer_id: customerId,
    });
    if (error && (error.code === '42883' || error.message?.includes('set_default_customer_address'))) {
      // Fallback: non-atomic two-step update for older deployments
      await supabase
        .from('customer_addresses')
        .update({ is_default: false })
        .eq('customer_id', customerId);
      return supabase
        .from('customer_addresses')
        .update({ is_default: true, updated_at: new Date().toISOString() })
        .eq('id', addressId)
        .eq('customer_id', customerId);
    }
    return { data, error };
  }
};

/**
 * ============================================================================
 * CUSTOMER FAVORITES OPERATIONS
 * ============================================================================
 */
export const customerFavoritesOperations = {
  /**
   * Get all favorite caterers
   */
  getFavorites: async (customerId, limit = 50) => {
    return supabase
      .from('customer_favorites')
      .select(`
        id,
        caterer_id,
        personal_notes,
        private_rating,
        times_ordered,
        total_spent,
        created_at,
        caterers (
          id,
          business_name,
          logo_url,
          verification_tier,
          location,
          caterer_stats (
            average_rating,
            total_reviews
          )
        )
      `)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false })
      .limit(limit);
  },

  /**
   * Check if caterer is favorited
   */
  isFavorite: async (customerId, catererId) => {
    // Use maybeSingle to avoid 406 when favorite does not exist
    const { data } = await supabase
      .from('customer_favorites')
      .select('id')
      .eq('customer_id', customerId)
      .eq('caterer_id', catererId)
      .maybeSingle();

    return !!data;
  },

  /**
   * Add caterer to favorites
   */
  addFavorite: async (customerId, catererId, notes = null) => {
    return supabase
      .from('customer_favorites')
      .insert({
        customer_id: customerId,
        caterer_id: catererId,
        personal_notes: notes,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Update favorite (add notes, rating)
   */
  updateFavorite: async (customerId, catererId, updateData) => {
    return supabase
      .from('customer_favorites')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('customer_id', customerId)
      .eq('caterer_id', catererId);
  },

  /**
   * Remove caterer from favorites
   */
  removeFavorite: async (customerId, catererId) => {
    return supabase
      .from('customer_favorites')
      .delete()
      .eq('customer_id', customerId)
      .eq('caterer_id', catererId);
  }
};

/**
 * ============================================================================
 * CATERER DISCOVERY OPERATIONS
 * ============================================================================
 */
export const customerCatererDiscoveryOperations = {
  /**
   * Search caterers with filters
   * All caterers are discoverable, verified ones ranked higher
   */
  searchCaterers: async (filters = {}) => {
    try {
      const deriveLocationTerms = (rawLocation) => {
        const value = String(rawLocation || '').trim();
        if (!value) return [];
        const parts = value
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);
        const deduped = [];
        const seen = new Set();
        for (const part of parts) {
          const normalized = part.toLowerCase();
          if (!seen.has(normalized)) {
            seen.add(normalized);
            deduped.push(part);
          }
        }
        if (deduped.length === 0) return [value];
        // Keep likely city/region tokens first, but include full input as fallback.
        return [...deduped, value];
      };

      // Radius search: when lat/lon/radius provided, use RPC for distance-based filter
      const lat = filters.latitude != null ? Number(filters.latitude) : null;
      const lon = filters.longitude != null ? Number(filters.longitude) : null;
      const radiusKm = filters.radiusKm != null ? Number(filters.radiusKm) : 50;
      if (lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon)) {
        const { data: rpcData, error: rpcError } = await supabase.rpc('search_caterers_near', {
          p_lat: lat,
          p_lon: lon,
          p_radius_km: radiusKm,
          p_search_query: filters.searchQuery?.trim() || null,
          p_event_type: filters.eventType && filters.eventType !== 'all' ? filters.eventType : null,
          p_verified_only: filters.verifiedOnly === true,
          p_min_rating: filters.minRating ?? null,
        });
        if (rpcError) {
          if (rpcError.code === '42883' || rpcError.message?.includes('does not exist')) {
            // RPC not yet deployed, fall through to text-based search
          } else {
            return { data: [], error: rpcError };
          }
        } else if (rpcData) {
          const result = { data: rpcData, error: null };
          if (result.data && result.data.length > 0) {
            const catererIds = result.data
              .filter(c => c.delivery_fee == null)
              .map(c => c.caterer_id || c.id);
            if (catererIds.length > 0) {
              const { data: deliveryFees } = await supabase
                .from('caterers')
                .select('id, delivery_fee, delivery_fee_enabled, free_delivery_threshold')
                .in('id', catererIds);
              if (deliveryFees) {
                const feeMap = new Map(deliveryFees.map(f => [f.id, f]));
                result.data = result.data.map(caterer => {
                  const feeData = feeMap.get(caterer.caterer_id || caterer.id);
                  if (feeData && caterer.delivery_fee == null) {
                    caterer.delivery_fee = feeData.delivery_fee;
                    caterer.delivery_fee_enabled = feeData.delivery_fee_enabled;
                    caterer.free_delivery_threshold = feeData.free_delivery_threshold;
                  }
                  return caterer;
                });
              }
            }
          }
          return result;
        }
      }

      // Use RPC (SECURITY DEFINER) to bypass 403/RLS on caterer_search_results view
      const searchQuery = filters.searchQuery ? String(filters.searchQuery).trim() : null;
      const radiusValues = ['5km', '10km', '25km', '50km'];
      const locationForFilter = filters.customerLocation || (
        filters.location && filters.location !== 'all' && !radiusValues.includes(String(filters.location).toLowerCase())
          ? filters.location
          : null
      );
      let locationTerms = null;
      if (locationForFilter && String(locationForFilter).trim()) {
        const rawLocation = String(locationForFilter).trim();
        try {
          const { terms } = await geocodePlace(rawLocation);
          locationTerms = terms.length > 0 ? terms : deriveLocationTerms(rawLocation);
        } catch (geoError) {
          console.warn('[searchCaterers] geocodePlace failed, using raw location fallback:', geoError?.message || geoError);
          locationTerms = deriveLocationTerms(rawLocation);
        }
      }

      let result = await supabase.rpc('get_caterer_search_results', {
        p_search_query: searchQuery || null,
        p_location_terms: locationTerms,
        p_event_type: filters.eventType && filters.eventType !== 'all' ? filters.eventType : null,
        p_verified_only: filters.verifiedOnly === true,
        p_min_rating: filters.minRating ?? null,
        p_sort_by: filters.sortBy || 'relevance',
        // Increase page size so "Load more" in the UI can show >50 without extra round-trips.
        p_limit: 200,
      });

      // Fallback: if RPC doesn't exist, try direct view (for older deployments)
      if (result.error && (result.error.code === '42883' || result.error.message?.includes('does not exist'))) {
        let query = supabase.from('caterer_search_results').select('*');
        if (searchQuery) query = query.or(`business_name.ilike."%${searchQuery.replace(/"/g, '""')}%",description.ilike."%${searchQuery.replace(/"/g, '""')}%"`);
        if (filters.eventType && filters.eventType !== 'all') query = query.contains('event_types', [filters.eventType]);
        if (filters.verifiedOnly) query = query.neq('verification_tier', 'none');
        if (filters.minRating) query = query.gte('average_rating', filters.minRating);
        query = query.order('is_sponsored', { ascending: false }).order('search_boost_multiplier', { ascending: false }).order('average_rating', { ascending: false });
        result = await query.limit(50);
      }

      if (result.error && (result.error.code === '42P01' || result.error.message?.includes('does not exist'))) {
        return { data: [], error: null };
      }

    // Blocked caterers are excluded by view caterer_search_results (migration 020)

    // Enrich with delivery_fee from caterers table if missing from view
    if (result.data && result.data.length > 0) {
      const catererIds = result.data
        .filter(c => c.delivery_fee == null)
        .map(c => c.caterer_id || c.id);
      
      if (catererIds.length > 0) {
        const { data: deliveryFees } = await supabase
          .from('caterers')
          .select('id, delivery_fee, delivery_fee_enabled, free_delivery_threshold')
          .in('id', catererIds);
        
        if (deliveryFees) {
          const feeMap = new Map(deliveryFees.map(f => [f.id, f]));
          result.data = result.data.map(caterer => {
            const feeData = feeMap.get(caterer.caterer_id || caterer.id);
            if (feeData && caterer.delivery_fee == null) {
              caterer.delivery_fee = feeData.delivery_fee;
              caterer.delivery_fee_enabled = feeData.delivery_fee_enabled;
              caterer.free_delivery_threshold = feeData.free_delivery_threshold;
            }
            return caterer;
          });
        }
      }
    }

    // Cuisine filter (post-query; RPC doesn't support it)
    if (result.data && filters.cuisineTypes && filters.cuisineTypes.length > 0) {
      result.data = result.data.filter(c => {
        const cuisines = Array.isArray(c.cuisine_types) ? c.cuisine_types : [];
        return filters.cuisineTypes.every((cuisine) => cuisines.includes(cuisine));
      });
    }

    // Filter by price if specified (post-query filtering)
    if (result.data && filters.maxPricePerPerson) {
      result.data = result.data.filter(c => {
        // Would need actual price data to filter
        return true;
      });
    }

    return result;
    } catch (err) {
      console.error('[searchCaterers] Error:', err);
      return { data: [], error: err };
    }
  },

  /**
   * Get single caterer details
   */
  getCatererDetails: async (catererId) => {
    // Use RPC (SECURITY DEFINER) to bypass 403/RLS on caterer_search_results
    let searchData = null;
    let searchError = null;
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_caterer_details', { p_caterer_id: catererId });
    if (rpcError) {
      if (rpcError.code === '42883' || rpcError.message?.includes('does not exist')) {
        const fallback = await supabase.from('caterer_search_results').select('*').eq('caterer_id', catererId).maybeSingle();
        searchData = fallback.data;
        searchError = fallback.error;
      } else {
        searchError = rpcError;
      }
    } else if (rpcData && rpcData.length > 0) {
      searchData = rpcData[0];
    }

    if (searchError || !searchData) {
      return { data: null, error: searchError };
    }

    // Fetch delivery/pickup options and delivery_fee from caterers table (view may not have all columns)
    const { data: catererData } = await supabase
      .from('caterers')
      .select('delivery_fee, delivery_fee_enabled, free_delivery_threshold, offers_delivery, offers_pickup')
      .eq('id', catererId)
      .maybeSingle();

    if (catererData) {
      if (searchData.delivery_fee == null) searchData.delivery_fee = catererData.delivery_fee;
      searchData.delivery_fee_enabled = catererData.delivery_fee_enabled ?? searchData.delivery_fee_enabled;
      searchData.free_delivery_threshold = catererData.free_delivery_threshold ?? searchData.free_delivery_threshold;
      searchData.offers_delivery = catererData.offers_delivery !== false;
      searchData.offers_pickup = catererData.offers_pickup !== false;
    } else {
      searchData.offers_delivery = searchData.offers_delivery !== false;
      searchData.offers_pickup = searchData.offers_pickup !== false;
    }

    return { data: searchData, error: null };
  },

  /**
   * Get caterer menu
   */
  getCatererMenu: async (catererId) => {
    return supabase
      .from('menu_items')
      .select('*')
      .eq('caterer_id', catererId)
      .order('category', { ascending: true })
      .order('name', { ascending: true });
  },

  /**
   * Get menu add-ons for a caterer (grouped by menu_item_id on the client)
   */
  getMenuAddons: async (catererId) => {
    return supabase
      .from('menu_addons')
      .select('*')
      .eq('caterer_id', catererId)
      .order('menu_item_id')
      .order('name');
  },

  /**
   * Get caterer reviews with reviewer details and order/event context
   */
  getCatererReviews: async (catererId, limit = 20) => {
    return supabase
      .from('reviews')
      .select(`
        *,
        customer:users (
          id,
          email,
          customer_profiles (
            full_name,
            profile_image_url
          )
        ),
        order:orders (
          id,
          events (
            event_type,
            estimated_guests,
            title
          )
        )
      `)
      .eq('caterer_id', catererId)
      .order('created_at', { ascending: false })
      .limit(limit);
  },

  /**
   * Get caterer portfolio
   */
  getCatererPortfolio: async (catererId) => {
    return supabase
      .from('portfolio_photos')
      .select('*')
      .eq('caterer_id', catererId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: false });
  },

  /**
   * Get caterer availability
   */
  getCatererAvailability: async (catererId) => {
    return supabase
      .from('availability_blocks')
      .select('*')
      .eq('caterer_id', catererId)
      .gte('block_date', new Date().toISOString().split('T')[0])
      .order('block_date', { ascending: true });
  },

  /**
   * Send inquiry to caterer
   */
  sendInquiry: async (customerId, catererId, inquiryData) => {
    return supabase
      .from('messages')
      .insert({
        sender_id: customerId,
        recipient_id: catererId,
        subject: inquiryData.subject,
        body: inquiryData.message,
        related_event_id: inquiryData.event_id,
        created_at: new Date().toISOString()
      });
  }
};

/**
 * ============================================================================
 * CUSTOMER EVENT OPERATIONS
 * ============================================================================
 */
export const customerEventOperations = {
  /**
   * Create new event
   */
  createEvent: async (customerId, eventData) => {
    return supabase
      .from('events')
      .insert({
        customer_id: customerId,
        title: eventData.title,
        description: eventData.description,
        event_type: eventData.event_type,
        event_date: eventData.event_date,
        event_time: eventData.event_time,
        estimated_guests: eventData.estimated_guests,
        location: eventData.location,
        venue_details: eventData.venue_details,
        budget_min: eventData.budget_min,
        budget_max: eventData.budget_max,
        special_requirements: eventData.special_requirements,
        dietary_restrictions: eventData.dietary_restrictions,
        status: 'open',
        created_at: new Date().toISOString()
      });
  },

  /**
   * Get customer events
   */
  getEvents: async (customerId) => {
    return supabase
      .from('events')
      .select(`
        *,
        bids (
          id,
          status,
          amount,
          caterer:caterers (
            business_name,
            logo_url,
            verification_tier
          )
        )
      `)
      .eq('customer_id', customerId)
      .order('event_date', { ascending: true });
  },

  /**
   * Get single event by id (customer's own)
   */
  getEventById: async (eventId, customerId) => {
    return supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .eq('customer_id', customerId)
      .single();
  },

  /**
   * Get single event with bids
   */
  getEventWithBids: async (eventId, customerId) => {
    return supabase
      .from('events')
      .select(`
        *,
        bids (
          id,
          caterer_id,
          status,
          amount,
          description,
          created_at,
          caterers (
            id,
            business_name,
            logo_url,
            location,
            verification_tier,
            phone,
            email,
            caterer_stats (
              average_rating,
              total_reviews,
              completion_rate,
              trust_score
            )
          )
        )
      `)
      .eq('id', eventId)
      .eq('customer_id', customerId)
      .single();
  },

  /**
   * Update event
   */
  updateEvent: async (eventId, customerId, eventData) => {
    return supabase
      .from('events')
      .update({
        ...eventData,
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .eq('customer_id', customerId);
  },

  /**
   * Close event to new bids
   */
  closeEvent: async (eventId, customerId) => {
    return supabase
      .from('events')
      .update({
        status: 'closed',
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .eq('customer_id', customerId);
  },

  /**
   * Cancel event (request fee will be forfeited)
   */
  cancelEvent: async (eventId, customerId) => {
    return supabase
      .from('events')
      .update({
        status: 'cancelled',
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .eq('customer_id', customerId);
  },

  /**
   * Get upcoming events
   */
  getUpcomingEvents: async (customerId, limit = 10) => {
    const today = new Date().toISOString().split('T')[0];
    
    return supabase
      .from('events')
      .select(`
        *,
        caterer:caterers (
          id,
          business_name,
          logo_url,
          verification_tier,
          caterer_stats (
            average_rating,
            total_reviews
          )
        )
      `)
      .eq('customer_id', customerId)
      .gte('event_date', today)
      .neq('status', 'cancelled')
      .order('event_date', { ascending: true })
      .limit(limit);
  },

  /**
   * Get customer's special requests
   */
  getCustomerRequests: async (customerId) => {
    return supabase
      .from('events')
      .select(`
        *,
        proposal_count:bids(count)
      `)
      .eq('customer_id', customerId)
      .eq('is_special_request', true)
      .order('created_at', { ascending: false });
  },

  /**
   * Calculate request fee based on budget range
   */
  calculateRequestFee: (budgetMin, budgetMax) => {
    if (!budgetMax) {
      // Above R100,000
      return 200;
    }
    if (budgetMax <= 10000) {
      // Under R10,000
      return 50;
    }
    if (budgetMax <= 50000) {
      // R10,000 - R50,000
      return 100;
    }
    // R50,000 - R100,000
    return 200;
  },

  /**
   * Create special request (with fee payment)
   */
  createSpecialRequest: async (customerId, requestData) => {
    // Events table: special_requirements (TEXT). Migration 010 adds is_special_request. Migration 039 adds recurrence.
    return supabase
      .from('events')
      .insert({
        customer_id: customerId,
        title: requestData.title,
        description: requestData.description,
        event_type: requestData.event_type,
        event_date: requestData.event_date,
        event_time: requestData.event_time ?? null,
        estimated_guests: requestData.guest_count,
        location: requestData.location,
        location_latitude: requestData.location_latitude ?? null,
        location_longitude: requestData.location_longitude ?? null,
        budget_min: requestData.budget_min,
        budget_max: requestData.budget_max,
        special_requirements: typeof requestData.requirements === 'string' ? requestData.requirements : JSON.stringify(requestData.requirements || {}),
        is_special_request: true,
        status: 'open',
        request_fee: requestData.request_fee || 0,
        request_fee_paid: requestData.request_fee_paid || false,
        request_fee_payment_ref: requestData.request_fee_payment_ref || null,
        request_fee_paid_at: requestData.request_fee_paid_at || null,
        recurrence_frequency: requestData.recurrence_frequency || null,
        recurrence_end_date: requestData.recurrence_end_date || null,
        parent_event_id: requestData.parent_event_id || null,
      })
      .select('id')
      .single();
  },

  /**
   * Create recurring event occurrences (same details, different dates). Call after first event is created and paid.
   * firstEventId = id of the event already created; basePayload = same shape as createSpecialRequest (with recurrence_*).
   */
  createRecurringOccurrences: async (customerId, firstEventId, basePayload) => {
    const freq = basePayload.recurrence_frequency;
    const endDate = basePayload.recurrence_end_date ? new Date(basePayload.recurrence_end_date) : null;
    if (!freq || !endDate || endDate <= new Date(basePayload.event_date)) return { data: [], error: null };
    const start = new Date(basePayload.event_date);
    const events = [];
    let d = new Date(start);
    const addOne = () => {
      if (freq === 'weekly') d.setDate(d.getDate() + 7);
      else if (freq === 'biweekly') d.setDate(d.getDate() + 14);
      else if (freq === 'monthly') d.setMonth(d.getMonth() + 1);
      else return;
    };
    addOne(); // skip first (already created)
    while (d <= endDate) {
      events.push({
        customer_id: customerId,
        title: basePayload.title,
        description: basePayload.description,
        event_type: basePayload.event_type,
        event_date: d.toISOString().slice(0, 10),
        event_time: basePayload.event_time ?? null,
        estimated_guests: basePayload.guest_count,
        location: basePayload.location,
        location_latitude: basePayload.location_latitude ?? null,
        location_longitude: basePayload.location_longitude ?? null,
        budget_min: basePayload.budget_min,
        budget_max: basePayload.budget_max,
        special_requirements: typeof basePayload.requirements === 'string' ? basePayload.requirements : JSON.stringify(basePayload.requirements || {}),
        is_special_request: true,
        status: 'open',
        request_fee: 0,
        request_fee_paid: true,
        request_fee_payment_ref: null,
        request_fee_paid_at: new Date().toISOString(),
        parent_event_id: firstEventId,
        recurrence_frequency: basePayload.recurrence_frequency,
        recurrence_end_date: basePayload.recurrence_end_date,
      });
      addOne();
    }
    if (events.length === 0) return { data: [], error: null };
    return supabase.from('events').insert(events).select('id');
  },

  /**
   * Upload one special-request attachment to Storage. Path: special-request-attachments/{eventId}/{timestamp}-{filename}.
   * Returns { path, url, name, size, type } for storing in event special_requirements.uploadedFiles.
   */
  uploadSpecialRequestAttachment: async (eventId, file) => {
    const sanitize = (s) => String(s).replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
    const ext = (file.name && file.name.includes('.')) ? file.name.split('.').pop() : 'bin';
    const fileName = `${Date.now()}-${sanitize(file.name || 'file')}`;
    const path = `${eventId}/${fileName}`;
    const { data, error } = await supabase.storage
      .from('special-request-attachments')
      .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
    if (error) return { error };
    const { data: urlData } = supabase.storage.from('special-request-attachments').getPublicUrl(data.path);
    return {
      path: data.path,
      url: urlData?.publicUrl ?? null,
      name: file.name,
      size: file.size,
      type: file.type || '',
    };
  },

  /**
   * Update only special_requirements for an event (e.g. after uploading attachments).
   */
  updateEventSpecialRequirements: async (eventId, customerId, specialRequirements) => {
    const payload = typeof specialRequirements === 'string' ? specialRequirements : JSON.stringify(specialRequirements || {});
    return supabase
      .from('events')
      .update({ special_requirements: payload, updated_at: new Date().toISOString() })
      .eq('id', eventId)
      .eq('customer_id', customerId);
  },

  /**
   * Update event with payment confirmation
   */
  confirmRequestFeePayment: async (eventId, customerId, paymentRef) => {
    return supabase
      .from('events')
      .update({
        request_fee_paid: true,
        request_fee_payment_ref: paymentRef,
        request_fee_paid_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .eq('customer_id', customerId);
  },

  /**
   * Refund request fee (when order is completed)
   */
  refundRequestFee: async (eventId) => {
    return supabase
      .from('events')
      .update({
        request_fee_refunded: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', eventId)
      .eq('request_fee_paid', true)
      .eq('request_fee_refunded', false);
  },

  /**
   * Get request fee credit for an event (for deducting from order total when paying).
   * Returns { requestFeeCredit } when event has request_fee_paid and !request_fee_refunded.
   */
  getEventRequestFeeCredit: async (eventId) => {
    const { data, error } = await supabase
      .from('events')
      .select('request_fee, request_fee_paid, request_fee_refunded')
      .eq('id', eventId)
      .maybeSingle();
    if (error || !data) return { data: null, error };
    const credit =
      data.request_fee_paid === true &&
      data.request_fee_refunded !== true &&
      Number(data.request_fee) > 0
        ? Number(data.request_fee)
        : 0;
    return { data: { requestFeeCredit: credit, requestFee: Number(data.request_fee || 0) }, error: null };
  },
};

/**
 * ============================================================================
 * DIRECTORY CATERERS (pre-listed foodpreneurs)
 * ============================================================================
 */

export const directoryOperations = {
  /**
   * Lightweight search over directory_caterers for landing page / "Food near me".
   * Filters by name and/or address (case-insensitive), limited result set.
   */
  getDirectoryCaterers: async ({ searchQuery, location, limit = 12 } = {}) => {
    try {
      let query = supabase.from('directory_caterers').select('id, name, phone, email, address, place_id, source');
      const trimmedSearch = searchQuery && String(searchQuery).trim();
      const trimmedLocation = location && String(location).trim();

      if (trimmedSearch) {
        const q = trimmedSearch.replace(/"/g, '""');
        query = query.or(`name.ilike."%${q}%",address.ilike."%${q}%"`);
      }
      if (trimmedLocation) {
        const loc = trimmedLocation.replace(/"/g, '""');
        query = query.or(`address.ilike."%${loc}%"`);
      }

      query = query.order('created_at', { ascending: false }).limit(limit);
      const { data, error } = await query;
      if (error) {
        // If table doesn't exist yet, fail silently with empty list.
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          return { data: [], error: null };
        }
        return { data: [], error };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('[getDirectoryCaterers] Error:', err);
      return { data: [], error: err };
    }
  },
};

/**
 * ============================================================================
 * BID OPERATIONS (Customer View)
 * ============================================================================
 */
export const customerBidOperations = {
  /**
   * Get all bids for customer's event
   */
  getEventBids: async (eventId, customerId) => {
    return supabase
      .from('customer_event_bids')
      .select('*')
      .eq('event_id', eventId);
  },

  /**
   * Get all bids/proposals received by customer
   */
  getReceivedBids: async (customerId) => {
    return supabase
      .from('bids')
      .select(`
        *,
        caterer:caterers (
          id,
          business_name,
          logo_url,
          verification_tier,
          location,
          specialties,
          response_time,
          caterer_stats (
            average_rating,
            total_reviews,
            completed_events
          )
        ),
        event:events!inner (
          id,
          title,
          event_type,
          customer_id
        )
      `)
      .eq('event.customer_id', customerId)
      .order('created_at', { ascending: false });
  },

  /**
   * Accept bid (create order from bid and close event)
   * Uses Edge Function for atomic, validated flow (prevents race conditions, validates ownership)
   * Calls function with explicit Bearer token (same pattern as confirm-delivery) to avoid 401.
   */
  acceptBid: async (bidId, customerId, eventId) => {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) return { data: null, error: { message: 'Session expired. Please sign in again.' } };
    const session = refreshData?.session ?? (await supabase.auth.getSession()).data?.session;
    const accessToken = session?.access_token;
    if (!accessToken) return { data: null, error: { message: 'Please sign in again to accept a bid.' } };

    const res = await fetch(`${SUPABASE_URL}/functions/v1/accept-bid`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ bidId, eventId }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { data: null, error: { message: data?.error || data?.detail || `Request failed (${res.status})` } };
    if (!data?.success) return { data: null, error: { message: data?.error || 'Failed to accept bid' } };
    return { data: { id: data.orderId }, error: null };
  },

  /**
   * Update bid (customer can edit/update bid details)
   */
  updateBid: async (bidId, customerId, updateData) => {
    // Verify bid belongs to customer's event
    const { data: bid } = await supabase
      .from('bids')
      .select('event_id, events!inner(customer_id)')
      .eq('id', bidId)
      .single();

    if (!bid || bid.events?.customer_id !== customerId) {
      return { data: null, error: { message: 'Bid not found or unauthorized' } };
    }

    return supabase
      .from('bids')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', bidId);
  },

  /**
   * Delete bid (customer can delete a bid)
   */
  deleteBid: async (bidId, customerId) => {
    // Verify bid belongs to customer's event
    const { data: bid } = await supabase
      .from('bids')
      .select('event_id, events!inner(customer_id)')
      .eq('id', bidId)
      .single();

    if (!bid || bid.events?.customer_id !== customerId) {
      return { data: null, error: { message: 'Bid not found or unauthorized' } };
    }

    return supabase
      .from('bids')
      .delete()
      .eq('id', bidId);
  },

  /**
   * Reject bid – uses Edge Function for validated flow
   */
  rejectBid: async (bidId, reason = null) => {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) return { data: null, error: { message: 'Session expired. Please sign in again.' } };
    const session = refreshData?.session ?? (await supabase.auth.getSession()).data?.session;
    const accessToken = session?.access_token;
    if (!accessToken) return { data: null, error: { message: 'Please sign in to reject a bid.' } };

    const res = await fetch(`${SUPABASE_URL}/functions/v1/reject-bid`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ bidId, reason: reason || 'Rejected by customer' }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { data: null, error: { message: data?.error || `Request failed (${res.status})` } };
    if (!data?.success) return { data: null, error: { message: data?.error || 'Failed to reject bid' } };
    return { data: { success: true }, error: null };
  },

  /**
   * Request counter-bid (message to caterer)
   */
  requestCounterBid: async (bidId, customerId, counterDetails) => {
    // First update bid
    const bidUpdate = await supabase
      .from('bids')
      .update({
        status: 'counter_offered',
        counter_details: counterDetails,
        counter_offered_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', bidId);

    if (bidUpdate.error) return bidUpdate;

    // Send message to caterer
    const bid = await supabase.from('bids').select('caterer_id').eq('id', bidId).single();
    
    if (bid.data) {
      await supabase
        .from('messages')
        .insert({
          sender_id: customerId,
          recipient_id: bid.data.caterer_id,
          subject: 'Counter-offer on Bid',
          body: counterDetails,
          related_order_id: null,
          created_at: new Date().toISOString()
        });
    }

    return bidUpdate;
  }
};

/**
 * ============================================================================
 * CUSTOMER ORDER OPERATIONS
 * ============================================================================
 */
export const customerOrderOperations = {
  ACTIVE_CHAT_ORDER_STATUSES: ['pending', 'confirmed', 'in_progress', 'ready_for_delivery'],
  /**
   * Create a new order
   */
  createOrder: async (orderData) => {
    return supabase
      .from('orders')
      .insert({
        customer_id: orderData.customerId,
        caterer_id: orderData.catererId,
        status: orderData.status || 'pending',
        payment_status: orderData.paymentStatus || 'pending',
        items_count: orderData.itemsCount || 0,
        subtotal: orderData.subtotal || 0,
        tax: orderData.tax || 0,
        discount: orderData.discount || 0,
        total_amount: orderData.totalAmount,
        delivery_date: orderData.deliveryDate,
        delivery_time: orderData.deliveryTime,
        delivery_location: orderData.deliveryLocation,
        delivery_notes: orderData.deliveryNotes,
        dietary_requirements: orderData.dietaryRequirements,
        special_requests: orderData.specialRequests,
        created_at: new Date().toISOString()
      })
      .select('*')
      .single();
  },

  /**
   * Create order with items (new method with proper order items storage)
   */
  createOrderWithItems: async (orderData, cartItems) => {
    try {
      // Validate minimum order amount
      const MINIMUM_ORDER_TOTAL = 80;
      const totalAmount = Number(orderData.totalAmount) || 0;
      if (totalAmount < MINIMUM_ORDER_TOTAL) {
        return {
          data: null,
          error: new Error(`Order total must be at least R${MINIMUM_ORDER_TOTAL}. Current total: R${totalAmount.toFixed(2)}`)
        };
      }

      // delivery_date: DB may have NOT NULL; use today when null (e.g. pickup orders)
      const deliveryDate = orderData.deliveryDate || new Date().toISOString().split('T')[0];

      // 1. Create the order
      const orderResult = await supabase
        .from('orders')
        .insert({
          customer_id: orderData.customerId,
          caterer_id: orderData.catererId,
          status: orderData.status || 'pending',
          payment_status: orderData.paymentStatus || 'pending',
          items_count: orderData.itemsCount || 0,
          subtotal: orderData.subtotal || 0,
          tax: orderData.tax || 0,
          discount: orderData.discount || 0,
          total_amount: orderData.totalAmount,
          delivery_fee: orderData.deliveryFee ?? 0,
          delivery_date: deliveryDate,
          delivery_time: orderData.deliveryTime,
          delivery_location: orderData.deliveryLocation,
          delivery_notes: orderData.deliveryNotes,
          dietary_requirements: orderData.dietaryRequirements,
          special_requests: orderData.specialRequests,
          payment_method: orderData.paymentMethod || null,
          created_at: new Date().toISOString()
        })
        .select('*')
        .single();

      if (orderResult.error) {
        return orderResult;
      }

      // 2. Create order items (support both schemas: menu_item_name/price_per_person and name/unit_price)
      const orderItemsData = cartItems.map(item => {
        const price = Number.isFinite(item.pricePerPerson) ? item.pricePerPerson : 0;
        const subtotal = price * item.quantity;
        const itemName = item.name || 'Menu Item';
        return {
          order_id: orderResult.data.id,
          menu_item_id: item.id,
          name: itemName,
          menu_item_name: itemName,
          quantity: item.quantity,
          unit_price: price,
          price_per_person: price,
          subtotal: subtotal,
          category: item.category || null,
          platform_markup_rate: 0,
          platform_markup_amount: 0,
          created_at: new Date().toISOString()
        };
      });

      const itemsResult = await supabase
        .from('order_items')
        .insert(orderItemsData)
        .select('id');

      if (itemsResult.error) {
        console.error('Failed to insert order items:', itemsResult.error);
        
        // If it's a permission error, return more helpful error
        if (itemsResult.error.code === '42501') {
          return {
            data: null,
            error: {
              ...itemsResult.error,
              message: 'Permission denied: Please run fix-order-items-permissions.sql in Supabase to fix RLS policies',
              hint: 'Run the SQL file: src/caterer-app-endpoints/fix-order-items-permissions.sql'
            }
          };
        }
        
        // Don't fail the whole order if items insert fails for other reasons
        // Return the order but log the error
      }

      // 3. Insert order_item_addons for cart items that have addons
      const insertedOrderItems = itemsResult.data || [];
      for (let i = 0; i < cartItems.length; i++) {
        const cartItem = cartItems[i];
        if (!cartItem.addons || cartItem.addons.length === 0) continue;
        const orderItemRow = insertedOrderItems[i];
        if (!orderItemRow?.id) continue;
        const addonsPayload = cartItem.addons
          .filter((a) => (a.quantity || 0) > 0)
          .map((a) => ({
            order_item_id: orderItemRow.id,
            menu_addon_id: a.id,
            name: a.name,
            unit_price: Number(a.price) || 0,
            quantity: Number(a.quantity) || 1
          }));
        if (addonsPayload.length > 0) {
          const addonsResult = await supabase.from('order_item_addons').insert(addonsPayload);
          if (addonsResult.error) {
            console.error('Failed to insert order item addons:', addonsResult.error);
          }
        }
      }

      return orderResult;
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Update order payment information
   */
  updateOrderPayment: async (orderId, paymentData) => {
    return supabase
      .from('orders')
      .update({
        payment_status: paymentData.paymentStatus,
        payment_reference: paymentData.paymentReference,
        payment_method: paymentData.paymentMethod,
        status: paymentData.status,
        payment_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId);
  },

  /**
   * Get order items for a specific order
   */
  getOrderItems: async (orderId) => {
    return supabase
      .from('order_items')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });
  },
  /**
   * Get customer orders
   */
  getOrders: async (customerId, options = {}) => {
    let query = supabase
      .from('orders')
      .select(`
        *,
        caterer:caterers (
          id,
          business_name,
          logo_url,
          verification_tier
        ),
        events (
          id,
          title,
          event_type,
          event_date,
          event_time,
          location,
          estimated_guests,
          is_special_request
        )
      `)
      .eq('customer_id', customerId);
    
    if (options.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    return query.order('created_at', { ascending: false });
  },

  /**
   * Get single order details (includes order_items and event for special requests)
   */
  getOrderDetails: async (orderId) => {
    return supabase
      .from('orders')
      .select(`
        *,
        caterer:caterers (
          id,
          business_name,
          logo_url,
          verification_tier,
          phone,
          email,
          delivery_fee
        ),
        order_items (
          id,
          menu_item_id,
          menu_item_name,
          quantity,
          price_per_person,
          subtotal,
          category,
          special_instructions
        ),
        events (
          id,
          title,
          description,
          event_type,
          event_date,
          event_time,
          location,
          estimated_guests,
          special_requirements,
          is_special_request
        )
      `)
      .eq('id', orderId)
      .single();
  },

  /**
   * Check if customer has at least one order with a caterer
   */
  hasOrderWithCaterer: async (customerId, catererId) => {
    const { data, error } = await supabase
      .from('orders')
      .select('id')
      .eq('customer_id', customerId)
      .eq('caterer_id', catererId)
      .limit(1)
      .maybeSingle();
    return !error && !!data;
  },

  /**
   * Check if customer has at least one active order with a caterer
   * Active chat is limited to in-flight orders only.
   */
  hasActiveOrderWithCaterer: async (customerId, catererId) => {
    const { data, error } = await supabase
      .from('orders')
      .select('id')
      .eq('customer_id', customerId)
      .eq('caterer_id', catererId)
      .in('status', customerOrderOperations.ACTIVE_CHAT_ORDER_STATUSES)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    return !error && !!data;
  },

  /**
   * Get latest active order between customer and caterer.
   */
  getLatestActiveOrderWithCaterer: async (customerId, catererId) => {
    return supabase
      .from('orders')
      .select('id, status, created_at')
      .eq('customer_id', customerId)
      .eq('caterer_id', catererId)
      .in('status', customerOrderOperations.ACTIVE_CHAT_ORDER_STATUSES)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
  },

  /**
   * Resolve latest active order by caterer user_id.
   */
  getLatestActiveOrderWithCatererUser: async (customerId, catererUserId) => {
    const { data: caterer, error: catererError } = await supabase
      .from('caterers')
      .select('id')
      .eq('user_id', catererUserId)
      .maybeSingle();
    if (catererError || !caterer?.id) {
      return { data: null, error: catererError || null };
    }
    return customerOrderOperations.getLatestActiveOrderWithCaterer(customerId, caterer.id);
  },

  /**
   * Get caterer user_ids where customer has at least one order (for messaging restriction)
   */
  getCatererUserIdsWithOrders: async (customerId) => {
    const { data: orders, error } = await supabase
      .from('orders')
      .select('caterer_id')
      .eq('customer_id', customerId);
    if (error || !orders?.length) return new Set();
    const catererIds = [...new Set(orders.map((o) => o.caterer_id).filter(Boolean))];
    if (catererIds.length === 0) return new Set();
    const { data: caterers } = await supabase
      .from('caterers')
      .select('user_id')
      .in('id', catererIds);
    const userIds = new Set((caterers || []).map((c) => c.user_id).filter(Boolean));
    return userIds;
  },

  /**
   * Get caterer user_ids where customer has at least one active order.
   */
  getCatererUserIdsWithActiveOrders: async (customerId) => {
    const { data: orders, error } = await supabase
      .from('orders')
      .select('caterer_id')
      .eq('customer_id', customerId)
      .in('status', customerOrderOperations.ACTIVE_CHAT_ORDER_STATUSES);
    if (error || !orders?.length) return new Set();
    const catererIds = [...new Set(orders.map((o) => o.caterer_id).filter(Boolean))];
    if (catererIds.length === 0) return new Set();
    const { data: caterers } = await supabase
      .from('caterers')
      .select('user_id')
      .in('id', catererIds);
    const userIds = new Set((caterers || []).map((c) => c.user_id).filter(Boolean));
    return userIds;
  },

  /**
   * Get order documents
   */
  getOrderDocuments: async (orderId) => {
    return supabase
      .from('order_documents')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', { ascending: false });
  },

  /**
   * Add addon to order
   */
  addOrderAddon: async (orderId, addonId) => {
    return supabase
      .from('order_addons')
      .insert({
        order_id: orderId,
        addon_id: addonId,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Submit order verification
   */
  submitVerification: async (orderId, verificationData) => {
    return supabase
      .from('order_verifications')
      .insert({
        order_id: orderId,
        verification_items: verificationData.items,
        comments: verificationData.comments,
        overall_rating: verificationData.rating,
        verified_at: new Date().toISOString()
      });
  },

  /**
   * Get single order (legacy)
   */
  getOrder: async (orderId, customerId) => {
    return supabase
      .from('customer_orders_detail')
      .select('*')
      .eq('order_id', orderId)
      .eq('customer_id', customerId)
      .single();
  },

  /**
   * Get orders by status
   */
  getOrdersByStatus: async (customerId, status) => {
    return supabase
      .from('customer_orders_detail')
      .select('*')
      .eq('customer_id', customerId)
      .eq('status', status)
      .order('created_at', { ascending: false });
  },

  /**
   * Update order (special requests, delivery details)
   */
  updateOrder: async (orderId, customerId, updateData) => {
    return supabase
      .from('orders')
      .update({
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('customer_id', customerId);
  },

  /**
   * Request order modification
   */
  requestModification: async (orderId, customerId, modificationData) => {
    // Send message to caterer
    const order = await supabase
      .from('orders')
      .select('caterer_id')
      .eq('id', orderId)
      .single();

    if (order.data) {
      await supabase
        .from('messages')
        .insert({
          sender_id: customerId,
          recipient_id: order.data.caterer_id,
          subject: 'Modification Request',
          body: JSON.stringify(modificationData),
          related_order_id: orderId,
          created_at: new Date().toISOString()
        });
    }

    // Update order with modification request flag
    return supabase
      .from('orders')
      .update({
        special_requests: modificationData.details,
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('customer_id', customerId);
  },

  /**
   * Request refund/cancellation
   */
  requestCancellation: async (orderId, customerId, reason) => {
    // Send message to caterer
    const order = await supabase
      .from('orders')
      .select('caterer_id')
      .eq('id', orderId)
      .single();

    if (order.data) {
      await supabase
        .from('messages')
        .insert({
          sender_id: customerId,
          recipient_id: order.data.caterer_id,
          subject: 'Order Cancellation Request',
          body: reason,
          related_order_id: orderId,
          created_at: new Date().toISOString()
        });
    }

    // Note: Actual cancellation handled by caterer or admin
    return { success: true };
  },

  /**
   * Complete order and enable review
   */
  completeOrder: async (orderId, customerId) => {
    return supabase
      .from('orders')
      .update({
        status: 'completed',
        completed_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', orderId)
      .eq('customer_id', customerId);
  }
};

/**
 * ============================================================================
 * REVIEW & RATING OPERATIONS
 * ============================================================================
 */
export const customerReviewOperations = {
  /**
   * Create review for completed order
   */
  createReview: async (customerId, catererId, orderId, reviewData) => {
    return supabase
      .from('reviews')
      .insert({
        customer_id: customerId,
        caterer_id: catererId,
        order_id: orderId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        food_quality: reviewData.food_quality,
        service_quality: reviewData.service_quality,
        value_for_money: reviewData.value_for_money,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Get customer reviews
   */
  getReviews: async (customerId) => {
    return supabase
      .from('reviews')
      .select(`
        *,
        caterers (
          business_name,
          logo_url
        )
      `)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });
  },

  /**
   * Update review
   */
  updateReview: async (reviewId, customerId, reviewData) => {
    return supabase
      .from('reviews')
      .update({
        ...reviewData,
        updated_at: new Date().toISOString()
      })
      .eq('id', reviewId)
      .eq('customer_id', customerId);
  },

  /**
   * Delete review
   */
  deleteReview: async (reviewId, customerId) => {
    return supabase
      .from('reviews')
      .delete()
      .eq('id', reviewId)
      .eq('customer_id', customerId);
  },

  /**
   * Check if review exists for order
   */
  hasReviewForOrder: async (customerId, orderId) => {
    const result = await supabase
      .from('reviews')
      .select('id')
      .eq('customer_id', customerId)
      .eq('order_id', orderId)
      .single();

    return result.data ? true : false;
  },

  /**
   * Get review for a specific order (to show existing review or edit)
   */
  getReviewForOrder: async (customerId, orderId) => {
    const result = await supabase
      .from('reviews')
      .select('id, rating, comment, created_at')
      .eq('customer_id', customerId)
      .eq('order_id', orderId)
      .maybeSingle();

    return result;
  }
};

/**
 * ============================================================================
 * SAVED SEARCHES OPERATIONS
 * ============================================================================
 */
export const customerSavedSearchesOperations = {
  /**
   * Get saved searches
   */
  getSavedSearches: async (customerId) => {
    return supabase
      .from('saved_searches')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });
  },

  /**
   * Create saved search
   */
  createSavedSearch: async (customerId, searchData) => {
    return supabase
      .from('saved_searches')
      .insert({
        customer_id: customerId,
        name: searchData.name,
        description: searchData.description,
        filters: searchData.filters,
        notification_enabled: searchData.notification_enabled || false,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Update saved search
   */
  updateSavedSearch: async (searchId, customerId, searchData) => {
    return supabase
      .from('saved_searches')
      .update({
        ...searchData,
        updated_at: new Date().toISOString()
      })
      .eq('id', searchId)
      .eq('customer_id', customerId);
  },

  /**
   * Delete saved search
   */
  deleteSavedSearch: async (searchId, customerId) => {
    return supabase
      .from('saved_searches')
      .delete()
      .eq('id', searchId)
      .eq('customer_id', customerId);
  },

  /**
   * Mark search as used (update last_used_at)
   */
  markSearchUsed: async (searchId, customerId) => {
    return supabase
      .from('saved_searches')
      .update({
        last_used_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', searchId)
      .eq('customer_id', customerId);
  }
};

/**
 * ============================================================================
 * MESSAGING OPERATIONS
 * ============================================================================
 */
export const customerMessagingOperations = {
  /**
   * Get messages
   */
  getMessages: async (customerId, direction = 'both') => {
    let query = supabase.from('messages').select('*');

    if (direction === 'sent') {
      query = query.eq('sender_id', customerId);
    } else if (direction === 'received') {
      query = query.eq('recipient_id', customerId);
    } else {
      query = query.or(`sender_id.eq.${customerId},recipient_id.eq.${customerId}`);
    }

    return query
      .order('created_at', { ascending: false })
      .limit(100);
  },

  /**
   * Get messages for a specific order
   */
  getOrderMessages: async (orderId, customerId) => {
    return supabase
      .from('messages')
      .select('*')
      .eq('related_order_id', orderId)
      .or(`sender_id.eq.${customerId},recipient_id.eq.${customerId}`)
      .order('created_at', { ascending: true });
  },

  /**
   * Get conversation with specific caterer
   */
  getConversation: async (customerId, catererId) => {
    return supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${customerId},recipient_id.eq.${catererId}),and(sender_id.eq.${catererId},recipient_id.eq.${customerId})`)
      .order('created_at', { ascending: true });
  },

  /**
   * Send message
   */
  sendMessage: async (customerId, catererId, messageData) => {
    // Default subject if not provided
    const subject = messageData.subject || 
                    (messageData.order_id ? 'Order message' : 'Message');
    
    return supabase
      .from('messages')
      .insert({
        sender_id: customerId,
        recipient_id: catererId,
        subject: subject,
        body: messageData.body,
        related_order_id: messageData.order_id,
        related_event_id: messageData.event_id,
        created_at: new Date().toISOString()
      });
  },

  /**
   * Mark message as read
   */
  markAsRead: async (messageId) => {
    return supabase
      .from('messages')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', messageId);
  },

  /**
   * Get unread messages count
   */
  getUnreadCount: async (customerId) => {
    const result = await supabase
      .from('messages')
      .select('id', { count: 'exact' })
      .eq('recipient_id', customerId)
      .eq('is_read', false);

    return result.count || 0;
  }
};

/**
 * ============================================================================
 * SUPPORT THREADS (customer <-> platform; resolved threads disappear from UI)
 * ============================================================================
 */
export const customerSupportOperations = {
  getOrCreateThread: async (customerId, orderId = null) => {
    let q = supabase
      .from('support_threads')
      .select('*')
      .eq('customer_id', customerId)
      .is('resolved_at', null);
    if (orderId) q = q.eq('order_id', orderId);
    else q = q.is('order_id', null);
    const { data: existing } = await q.maybeSingle();
    if (existing) return { data: existing, error: null };
    const { data: created, error } = await supabase
      .from('support_threads')
      .insert({ customer_id: customerId, order_id: orderId || null })
      .select()
      .single();
    return { data: created, error };
  },

  getThreadForOrder: async (customerId, orderId) => {
    const { data, error } = await supabase
      .from('support_threads')
      .select('*')
      .eq('customer_id', customerId)
      .eq('order_id', orderId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    return { data, error };
  },

  getOpenThreadForOrder: async (customerId, orderId) => {
    const { data, error } = await supabase
      .from('support_threads')
      .select('*')
      .eq('customer_id', customerId)
      .eq('order_id', orderId)
      .is('resolved_at', null)
      .maybeSingle();
    return { data, error };
  },

  /** All open (unresolved) support threads for a customer, for global floating chat */
  getOpenThreads: async (customerId) => {
    const { data, error } = await supabase
      .from('support_threads')
      .select('*')
      .eq('customer_id', customerId)
      .is('resolved_at', null)
      .order('updated_at', { ascending: false });
    if (error) return { data: [], error };
    return { data: data ?? [], error: null };
  },

  getMessages: async (threadId) => {
    return supabase
      .from('support_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });
  },

  sendMessage: async (threadId, customerId, body) => {
    return supabase
      .from('support_messages')
      .insert({
        thread_id: threadId,
        sender_id: customerId,
        sender_type: 'customer',
        body: (body || '').trim(),
      })
      .select()
      .single();
  },
};

/**
 * ============================================================================
 * REAL-TIME SUBSCRIPTIONS
 * ============================================================================
 */
export const customerRealtimeOperations = {
  /**
   * Subscribe to order status updates
   */
  subscribeToOrderUpdates: (orderId, callback) => {
    return supabase
      .channel(`order:${orderId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to event bids
   */
  subscribeToEventBids: (eventId, callback) => {
    return supabase
      .channel(`event-bids:${eventId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids',
          filter: `event_id=eq.${eventId}`
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to messages
   */
  subscribeToMessages: (customerId, callback) => {
    return supabase
      .channel(`messages:${customerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${customerId}`
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to support thread updates (e.g. resolved_at)
   */
  subscribeToSupportThread: (threadId, callback) => {
    return supabase
      .channel(`support-thread:${threadId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'support_threads', filter: `id=eq.${threadId}` },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to support messages (new replies)
   */
  subscribeToSupportMessages: (threadId, callback) => {
    return supabase
      .channel(`support-messages:${threadId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `thread_id=eq.${threadId}` },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to orders for a customer (new orders + status updates)
   */
  subscribeToOrdersForCustomer: (customerId, callback) => {
    return supabase
      .channel(`orders:customer:${customerId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `customer_id=eq.${customerId}`
        },
        callback
      )
      .subscribe();
  },

  /**
   * Subscribe to platform notifications (admin broadcasts). Filter in callback for customers/all.
   */
  subscribeToPlatformNotifications: (callback) => {
    return supabase
      .channel('platform-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'platform_notifications',
        },
        (payload) => {
          const row = payload?.new;
          if (!row) return;
          const rt = row?.recipient_type;
          if (rt === 'all' || rt === 'customers') {
            callback(row);
          }
        }
      )
      .subscribe();
  },

  /**
   * Subscribe to all new bids (for notification). Callback receives (payload, supabase) so caller can check event.customer_id.
   */
  subscribeToNewBids: (callback) => {
    return supabase
      .channel('bids:all:insert')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids'
        },
        (payload) => callback(payload, supabase)
      )
      .subscribe();
  },

  /**
   * Unsubscribe from channel
   */
  unsubscribe: (channel) => {
    return supabase.removeChannel(channel);
  }
};

/**
 * ============================================================================
 * UTILITY OPERATIONS
 * ============================================================================
 */
export const customerUtilityOperations = {
  /**
   * Upload profile image
   */
  uploadProfileImage: (customerId, file) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${customerId}-profile-${Date.now()}.${fileExt}`;
    const filePath = `profile-images/${customerId}/${fileName}`;

    return supabase.storage
      .from('profile-images')
      .upload(filePath, file);
  },

  /**
   * Get signed URL for image
   */
  getSignedImageUrl: async (bucket, filePath, expiresIn = 3600) => {
    return supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, expiresIn);
  },

  /**
   * Delete profile image
   */
  deleteProfileImage: (filePath) => {
    return supabase.storage
      .from('profile-images')
      .remove([filePath]);
  },
};

export default supabase;
